import React, { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useEditor } from "@tiptap/react"
import Split from "react-split"
import { ConfigProvider } from "antd"
import { useNavigate } from "react-router-dom"

import Document from "@tiptap/extension-document"
import Text from "@tiptap/extension-text"
import Paragraph from "@tiptap/extension-paragraph"
import History from "@tiptap/extension-history"
import Placeholder from "@tiptap/extension-placeholder"
import { CommandSuggestion } from "features/QueryConsole/components/Extensions/CommandSuggestionExtension"
import { QC_THEME } from "features/QueryConsole/components/qcTheme"
import QuerySyntaxHighlight from "features/QueryConsole/components/Extensions/QuerySyntaxHighlightingExtension"
import QueryIndent from "features/QueryConsole/components/Extensions/QueryIndentExtension"
import SmartClipboardExtension from "shared/TipTap/Extensions/SmartClipboardExtension"

import { useProjectStore, Project } from "stores/projectStore"
import { PROJECT_TAB_KEYS } from "constants/projectTabs"
import { useQueryConsoleLayoutStore } from "stores/queryConsoleLayoutStore"
import { useTableStyleStore } from "stores/tableStyleStore"
import { useWeightingReportStore } from "stores/weightingReportStore"
import {
  useBatchBuilderScript,
  useBuilderScript,
  useQueryBuilderStore,
  buildTransformPrefix,
} from "stores/queryBuilderStore"
import {
  buildWorkbook,
  downloadWorkbook,
  TableExport,
} from "utils/excel/exportTableModels"
import { buildTableModel } from "utils/tableModel"
import { resolveTableStyle } from "shared/ui/Table/style/looks"
import { RTableContent, WeightingReport } from "types/engineOutput.types"
import { useTransformBackend } from "features/QueryConsole/hooks/useTransformBackend"

import Console from "features/QueryConsole/components/Console.component"
import QueryConsoleToolbar from "features/QueryConsole/components/QueryConsoleToolbar.component"
import QueryBuilder from "features/QueryConsole/components/Builder/QueryBuilder.component"
import TransformView from "features/QueryConsole/components/Transform/TransformView.component"
import DataTree from "features/QueryConsole/components/DataTree.component"
import SavedQueries from "features/QueryConsole/components/SavedQueries/SavedQueries.component"
import { LineageModal, provenanceLink } from "features/Provenance"
import RunOnLoadModal from "features/QueryConsole/components/RunOnLoad/RunOnLoadModal.component"
import RunOnLoadBanner from "features/QueryConsole/components/RunOnLoad/RunOnLoadBanner.component"
import {
  LIST_NOT_CHECKED,
  RunOnLoadListError,
  useRunOnLoadList,
} from "features/QueryConsole/components/RunOnLoad/useRunOnLoadList"
import EmptyState from "shared/ui/EmptyState/EmptyState.component"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import { notify } from "shared/ui/Notifications/notify"

import { useEngine } from "hooks/api/useEngine"
import { useSavedQueries } from "features/QueryConsole/api/useSavedQueries"
import {
  ConsoleHandoff,
  scriptToEditorHtml,
  useConsoleHandoffStore,
} from "features/QueryConsole/consoleHandoff"
import {
  AssistantDock,
  AssistantScreenRegistrar,
  AssistantScreenRegistration,
  AssistantToggle,
  selectConversation,
  useAssistantStore,
} from "features/Assistant"
import { useDataImport } from "features/DataImport/api/useDataImport"
import { useAsync } from "hooks/core/useAsync"
import { useRunWhenIdle } from "features/QueryConsole/hooks/useRunWhenIdle"
import handleSequenceError from "utils/handleSequenceError"
import { QueryInstance } from "types/engine.types"
import { DATAFILE_TYPE } from "types/dataImport.types"
import { CodebookData } from "types/codebook.types"
import { EngineQueryResult } from "types/engineOutput.types"
import { isReservedQueryFileName } from "types/savedQuery.types"
import {
  readInitialiseReport,
  runOnLoadFailureText,
  type InitialiseMode,
  type RunOnLoadFailure,
} from "types/runOnLoad.types"
import { AxiosResponse } from "axios"

import "styles/queryConsole.css"

declare global {
  interface Window {
    RunEngineQuery: (
      instanceID: QueryInstance,
      query: string,
      isAsync: boolean
    ) => Promise<AxiosResponse<unknown>>
    queryInstance: QueryInstance | null
  }
}

interface QueryConsoleContainerProps {
  activeKey?: string
}

// Tab key of the "Data Reporting" tab in ProjectDetailsTabs. Used to detect
// the user returning to this tab so we can check for re-imported data.
const DATA_REPORTING_TAB_KEY = PROJECT_TAB_KEYS.DATA_REPORTING

/**
 * The Console as the assistant sees it: no context of its own (the assistant
 * reads the project through its tools), and nothing to review here. Script
 * proposals are saved from their cards on every screen.
 */
const CONSOLE_ASSISTANT: AssistantScreenRegistration = {
  screen: "console",
  getContext: () => ({}),
  handlers: {},
}

/**
 * Container component for the Query Console.
 * Manages the workspace where users write, run, and save Data(Q) queries.
 *
 * Features:
 * - Three view modes (Builder placeholder, Console editor, Saved queries) via the tab ribbon.
 * - Persistent layout: hide/show DataTree, toggle vertical/horizontal split, swap editor/output order.
 * - Real-time script execution status with async polling.
 * - Codebook-driven variable tree.
 *
 * @component
 */
const QueryConsoleContainer: React.FC<QueryConsoleContainerProps> = ({
  activeKey,
}) => {
  const project: Project = useProjectStore((state) => state.project)
  const {
    initialiseData,
    isInitialisingData,
    RunEngineQuery,
    isRunningEngineQuery,
  } = useEngine()
  const { getDataFilesUploaded, isGettingDataFilesUploaded } = useDataImport()
  const {
    getProjectQueries,
    isLoadingQueries,
    deleteQuery,
    saveStructured,
  } = useSavedQueries()
  const { pollingAsync, getAsyncResult } = useAsync()

  // Core state
  const [result, setResult] = useState<EngineQueryResult>({})
  const [isRunningScript, setIsRunningScript] = useState<boolean>(false)
  const [isCodebookLoading, setIsCodebookLoading] = useState<boolean>(false)
  const [dataUploaded, setDataUploaded] = useState<boolean>(false)
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true)
  const [selectedQuery, setSelectedQuery] = useState<string>("")
  const [codebook, setCodebook] = useState<CodebookData>([])
  const [queryInstance, setQueryInstance] = useState<QueryInstance | null>(null)
  const [projectQueries, setProjectQueries] = useState<string[]>([])
  const [contentChanged, setContentChanged] = useState<boolean>(false)
  const [lineageOpen, setLineageOpen] = useState<boolean>(false)
  const navigate = useNavigate()
  // The lineage window reads the session once no script or codebook read runs there.
  const lineageInTurn = useRunWhenIdle(isRunningScript || isCodebookLoading)
  // A script handed over by the assistant's "Open in Console", and one
  // waiting for the user to agree to replace unsaved edits.
  const handoff = useConsoleHandoffStore((s) => s.pending)
  const [replacing, setReplacing] = useState<ConsoleHandoff | null>(null)
  // Scripts saved from the assistant's panel: the saved-query list is read again.
  const assistantSaves = useAssistantStore(
    (s) =>
      selectConversation(project.id)(s).proposals.filter(
        (p) => p.proposal.kind === "script" && p.status === "accepted"
      ).length
  )
  // Run on load: the project's list, the saved scripts it can take, the last open's failure, and
  // whether this session is without edits.
  const [onLoadList, setOnLoadList] = useState<string[]>([])
  const [onLoadListError, setOnLoadListError] = useState<string | null>(null)
  const [onLoadListReplaceable, setOnLoadListReplaceable] = useState<boolean>(false)
  const [onLoadAvailable, setOnLoadAvailable] = useState<string[]>([])
  const [onLoadFailure, setOnLoadFailure] = useState<RunOnLoadFailure | null>(null)
  const [withoutEdits, setWithoutEdits] = useState<boolean>(false)
  const [runOnLoadOpen, setRunOnLoadOpen] = useState<boolean>(false)
  const runOnLoad = useRunOnLoadList(project?.id ?? "", project?.Created_by ?? "")

  // Holds the project id an engine query instance was requested for. Guards
  // against duplicate initialise-data calls (StrictMode double effects,
  // re-renders): every duplicate creates another engine instance holding a
  // full copy of the project's dataset, which lingers server-side.
  const initialisedProjectRef = useRef<string | null>(null)

  // Size of the DQD file the current engine instance was initialised from,
  // and the previously seen tab key. Together these detect "user came back
  // to this tab and the data changed since we initialised" — the only case
  // where a re-initialise is worth creating a new engine instance for.
  const dataFileSizeRef = useRef<number | null>(null)
  const prevActiveKeyRef = useRef<string | undefined>(activeKey)

  // Layout state — persisted via Zustand
  const viewMode = useQueryConsoleLayoutStore((s) => s.viewMode)
  const setViewMode = useQueryConsoleLayoutStore((s) => s.setViewMode)
  const dataTreeVisible = useQueryConsoleLayoutStore((s) => s.dataTreeVisible)
  const consoleOutputDirection = useQueryConsoleLayoutStore(
    (s) => s.consoleOutputDirection
  )
  const consoleOutputOrder = useQueryConsoleLayoutStore(
    (s) => s.consoleOutputOrder
  )
  const horizontalSplitSizes = useQueryConsoleLayoutStore(
    (s) => s.horizontalSplitSizes
  )
  const setHorizontalSplitSizes = useQueryConsoleLayoutStore(
    (s) => s.setHorizontalSplitSizes
  )
  const consoleSplitSizes = useQueryConsoleLayoutStore(
    (s) => s.consoleSplitSizes
  )
  const setConsoleSplitSizes = useQueryConsoleLayoutStore(
    (s) => s.setConsoleSplitSizes
  )
  const isMaximized = useQueryConsoleLayoutStore((s) => s.isMaximized)
  const setMaximized = useQueryConsoleLayoutStore((s) => s.setMaximized)
  const consoleStructuredOrigin = useQueryConsoleLayoutStore(
    (s) => s.consoleStructuredOrigin
  )
  const setConsoleStructuredOrigin = useQueryConsoleLayoutStore(
    (s) => s.setConsoleStructuredOrigin
  )

  // Builder-derived script. Always evaluated, but only used when viewMode is
  // "builder". Cheap memoization inside the hook.
  const { script: builderScript, runnable: builderRunnable } = useBuilderScript()
  // "Run all" — concatenated script for every runnable builder, plus the
  // ordered builder ids that contributed an OUTPUT line. Used by `runBatch`
  // below; `runnableCount` drives the toolbar button's enabled state.
  const {
    script: batchScript,
    includedBuilderIds: batchBuilderIds,
    runnableCount: batchRunnableCount,
  } = useBatchBuilderScript()
  const syncBuilderToProject = useQueryBuilderStore((s) => s.syncToProject)
  const { hydrateFromBackend, savePipeline, saveBuilder } = useTransformBackend()

  // Bind the persisted Builder draft to this project. If the persisted draft
  // is from a different project, this resets it so stale variables can't
  // leak across projects. Then hydrate any pipelines/builders persisted to
  // the backend (and migrate localStorage-only ones up on first visit).
  useEffect(() => {
    if (!project?.id) return
    syncBuilderToProject(project.id)
    if (project.Created_by) {
      hydrateFromBackend(project.id, project.Created_by).catch((err) => {
        console.warn("Failed to hydrate transform backend state:", err)
      })
    }
    // Hydrate intentionally not in deps — it's stable per project, and
    // re-running on every render would thrash the listStructured fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, project?.Created_by, syncBuilderToProject])

  // Initialize editor
  const editor = useEditor({
    extensions: [
      Document,
      Text,
      Paragraph,
      History,
      SmartClipboardExtension,
      CommandSuggestion,
      QuerySyntaxHighlight,
      QueryIndent,
      Placeholder.configure({
        placeholder:
          "Start typing your query, or type / to see available commands.",
      }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "console-font",
        // Query keywords/variable names aren't English words — browser
        // spell-check just litters the editor with red squiggles.
        spellcheck: "false",
      },
    },
  })

  // Share RunEngineQuery and queryInstance with CommandSuggestion extension
  // (anti-pattern but necessary for that extension to access these)
  useEffect(() => {
    window.RunEngineQuery = RunEngineQuery
    window.queryInstance = queryInstance
  }, [RunEngineQuery, queryInstance])

  // Derived state
  const pageLoading: boolean =
    !editor ||
    isGettingDataFilesUploaded ||
    isInitialisingData ||
    isLoadingQueries ||
    isDataLoading
  const scriptInProgress: boolean = isRunningEngineQuery && isRunningScript
  const asyncScriptInProgress: boolean =
    !isRunningEngineQuery && isRunningScript

  useEffect(() => {
    getInitialData()
    fetchQueries()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When the user returns to this tab (e.g. after re-importing data in the
  // Data Import tab), re-initialise — but only if the DQD file actually
  // changed. A blind reload on every visit would create (and leak) another
  // engine query instance holding a full copy of the dataset each time.
  useEffect(() => {
    const cameBack =
      activeKey === DATA_REPORTING_TAB_KEY &&
      prevActiveKeyRef.current !== DATA_REPORTING_TAB_KEY
    prevActiveKeyRef.current = activeKey
    if (!cameBack || !project?.id || isDataLoading) return

    const reloadIfDataChanged = async (): Promise<void> => {
      try {
        const fetchRes = await getDataFilesUploaded(
          project.id,
          project.Created_by,
          DATAFILE_TYPE.DQD,
          "size"
        )
        const size = typeof fetchRes.data === "number" ? fetchRes.data : null
        if (size !== null && size !== dataFileSizeRef.current) {
          // Data changed since this instance was initialised: release the
          // duplicate guard and load fresh.
          initialisedProjectRef.current = null
          setIsDataLoading(true)
          await getInitialData()
        }
      } catch (error) {
        console.error("Error checking for data changes:", error)
      }
    }
    void reloadIfDataChanged()
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey])

  useEffect(() => {
    if (editor) {
      const updateContentChanged = (): void => {
        setContentChanged(true)
      }

      editor.on("update", updateContentChanged)
      return () => {
        editor.off("update", updateContentChanged)
      }
    }
  }, [editor])

  // Esc exits maximize mode. Only attached while maximized so we don't
  // swallow Esc for other contexts (modals, dropdowns).
  useEffect(() => {
    if (!isMaximized) return
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMaximized(false)
    }
    document.addEventListener("keydown", handleEsc)
    return () => document.removeEventListener("keydown", handleEsc)
  }, [isMaximized, setMaximized])

  /** Puts a handed-over script in the editor and shows the Console. */
  const loadHandoff = (h: ConsoleHandoff): void => {
    if (!editor) return
    editor.commands.clearContent()
    editor.commands.insertContent(scriptToEditorHtml(h.script))
    // A saved script opens as itself; a new one as an unsaved script.
    setSelectedQuery(h.name ?? "")
    setContentChanged(h.name === null)
    setConsoleStructuredOrigin(null)
    setViewMode("console")
  }

  // Take a script handed over for this project once the editor is there.
  // Unsaved edits in the editor are replaced only when the user agrees.
  useEffect(() => {
    if (!handoff || !editor || handoff.projectId !== project.id) return
    useConsoleHandoffStore.getState().clear()
    if (contentChanged && editor.getText().trim() !== "") setReplacing(handoff)
    else loadHandoff(handoff)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoff, editor, project.id])

  useEffect(() => {
    if (assistantSaves > 0) void fetchQueries()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assistantSaves])

  const getInitialData = async (mode: InitialiseMode = withoutEdits ? "off" : "report"): Promise<void> => {
    if (!project?.id || initialisedProjectRef.current === project.id) {
      // Already initialised (or initialising) for this project — a duplicate
      // call would create another engine query instance.
      setIsDataLoading(false)
      return
    }
    initialisedProjectRef.current = project.id
    // A weighting report belongs to the data it was run on, so a fresh open
    // (another project, or a reload) drops the last Apply's reports.
    useWeightingReportStore.getState().setReports([])
    try {
      const fetchRes = await getDataFilesUploaded(
        project.id,
        project.Created_by,
        DATAFILE_TYPE.DQD,
        "size"
      )

      if (typeof fetchRes.data === "number") {
        dataFileSizeRef.current = fetchRes.data
        const initialiseRes = await initialiseData(project.id, mode)
        const report = readInitialiseReport(initialiseRes.data)
        if (!report) {
          if (handleSequenceError(initialiseRes.data, "Error Initializing Data!")) {
            // handleSequenceError showed nothing (an "Error NNNN:" text, JSON without an instance): say so.
            const body = initialiseRes.data
            notify.error(
              "Error Initializing Data!",
              typeof body === "string" && body.trim() ? body.trim() : "The data could not be opened."
            )
          }
          // No instance was created — allow a later retry.
          initialisedProjectRef.current = null
        } else {
          // The Console opens with the scripts that ran and shows where Run on load stopped (spec R4).
          setOnLoadFailure(report.failed)
          const instanceId = report.instance as QueryInstance
          setQueryInstance(instanceId)
          setIsCodebookLoading(true)
          const codebookRes = await RunEngineQuery(
            instanceId,
            "CODEBOOK",
            false
          )
          if (
            handleSequenceError(codebookRes.data, "Error Creating Codebook!")
          ) {
            const codebookData = codebookRes.data as EngineQueryResult
            if (codebookData.result?.output?.[0]?.content) {
              const outputItem = codebookData.result.output[0]
              if (outputItem.content && "value" in outputItem.content) {
                setCodebook(outputItem.content.value as CodebookData)
                setDataUploaded(true)
              }
            }
          }
          setIsCodebookLoading(false)
        }
      } else {
        // No data file uploaded yet — no instance was created, so a later
        // call (e.g. after an upload) may initialise.
        initialisedProjectRef.current = null
      }
    } catch (fetchError) {
      initialisedProjectRef.current = null
      console.error("Error fetching data:", fetchError)
    } finally {
      setIsDataLoading(false)
    }
  }

  /** Opens the project's data again, so a changed Run on load list or script takes effect. */
  const reloadData = async (mode: InitialiseMode): Promise<void> => {
    // The loader replaces the page until the open ends, so no second reload or toggle starts meanwhile.
    setIsDataLoading(true)
    setOnLoadFailure(null)
    initialisedProjectRef.current = null
    setQueryInstance(null)
    await getInitialData(mode)
  }

  const toggleWithoutEdits = (): void => {
    const next = !withoutEdits
    setWithoutEdits(next)
    void reloadData(next ? "off" : "report")
  }

  const saveRunOnLoad = async (scripts: string[]): Promise<void> => {
    await runOnLoad.save(scripts)
    setOnLoadList(scripts)
    // Closed before the reload, so the loader does not unmount and remount it open.
    setRunOnLoadOpen(false)
    await fetchQueries()
    await reloadData(withoutEdits ? "off" : "report")
  }

  const regenerateCodebook = useCallback(async (): Promise<void> => {
    if (!queryInstance) {
      // Project not yet initialised; nothing to refresh against.
      return
    }
    try {
      setIsCodebookLoading(true)
      const codebookRes = await RunEngineQuery(
        queryInstance,
        "CODEBOOK",
        false
      )
      if (handleSequenceError(codebookRes.data, "Error Creating Codebook!")) {
        const codebookData = codebookRes.data as EngineQueryResult
        if (codebookData.result?.output?.[0]?.content) {
          const outputItem = codebookData.result.output[0]
          if (outputItem.content && "value" in outputItem.content) {
            setCodebook(outputItem.content.value as CodebookData)
          }
        }
      }
      setIsCodebookLoading(false)
    } catch (error) {
      console.error("Error regenerating codebook:", error)
      setIsCodebookLoading(false)
    }
  }, [RunEngineQuery, queryInstance])

  const fetchQueries = useCallback(async (): Promise<void> => {
    try {
      const queryRes = await getProjectQueries(project.id, project.Created_by)
      if (queryRes.data) {
        const queryData: string[] = queryRes.data
          .toString()
          .split("\n")
          .map((name: string) => name.trim())
          .filter((name: string) => !isReservedQueryFileName(name))
        setProjectQueries(queryData)
      } else {
        setProjectQueries([])
      }
    } catch (error) {
      console.error("Error fetching queries:", error)
    }
  }, [getProjectQueries, project.id, project.Created_by])

  // Read the Run on load list, and which saved queries are scripts it can take, whenever the saved
  // queries change.
  useEffect(() => {
    if (!project?.id) return
    let cancelled = false
    runOnLoad
      .read()
      .then((list) => {
        if (cancelled) return
        setOnLoadList(list)
        setOnLoadListError(null)
        setOnLoadListReplaceable(false)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // Shown as empty; the modal says why. A list that was read but does not parse can be
        // replaced by a save; one that could not be checked is saved over only by a changed draft.
        setOnLoadList([])
        setOnLoadListError(err instanceof Error ? err.message : LIST_NOT_CHECKED)
        setOnLoadListReplaceable(err instanceof RunOnLoadListError && err.replaceable)
      })
    const names = projectQueries.filter((n) => n.trim() !== "")
    runOnLoad
      .scriptsOf(names)
      .then((scripts) => {
        if (!cancelled) setOnLoadAvailable(scripts)
      })
      .catch(() => {
        // The kinds could not be read: offer the names, and `check` refuses each one it cannot check.
        if (!cancelled) setOnLoadAvailable(names)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id, projectQueries])

  /**
   * Returns the script to run for the current view. In Builder mode this is
   * the generated XTAB/DTAB; in Console mode the editor text.
   */
  const getCurrentScriptForRun = (): string => {
    if (viewMode === "builder") return builderScript
    if (!editor) return ""
    return editor.getText().replace(/\n\n+/g, "\n")
  }

  const runScript = async (): Promise<void> => {
    const script = getCurrentScriptForRun()
    if (!script.trim()) return

    // Snapshot where this run originated so the async result lands in the
    // correct place even if the user switches builders or view modes
    // before the engine responds.
    const runOriginIsBuilder = viewMode === "builder"
    const builderTargetId = runOriginIsBuilder
      ? useQueryBuilderStore.getState().activeBuilderId
      : null
    const setBuilderResult =
      useQueryBuilderStore.getState().setBuilderResult

    const applyResult = (data: EngineQueryResult): void => {
      if (runOriginIsBuilder && builderTargetId) {
        setBuilderResult(builderTargetId, data)
      } else {
        setResult(data)
      }
      // Codebook refresh is intentionally NOT auto-triggered here —
      // researchers control it explicitly via Transform → Apply or the
      // DataTree refresh button, which avoids cascading 502s when the
      // session is mid-flight.
    }

    try {
      setIsRunningScript(true)
      const result = await RunEngineQuery(
        queryInstance as QueryInstance,
        script,
        true
      )

      if (result.status === 202) {
        pollForAsyncResult(result.data as string, applyResult)
      } else {
        applyResult(result.data as EngineQueryResult)
        setIsRunningScript(false)
      }
    } catch (error) {
      console.error("An error occurred while running the script:", error)
      setIsRunningScript(false)
    }
  }

  /**
   * Run just the project-level custom variable definitions, to materialise
   * the FIELDs in the engine session so the codebook (and hence the data
   * tree) reflects them. The only output kept is the weighting report of any
   * CALCWEIGHTS, shown under its weighting step.
   */
  const runTransforms = async (): Promise<void> => {
    if (!queryInstance) {
      console.warn("Cannot apply transforms: project not yet initialised")
      return
    }
    const { transformPipelines, activeTransformPipelineId } =
      useQueryBuilderStore.getState()
    const pipeline = transformPipelines.find(
      (p) => p.id === activeTransformPipelineId
    )
    const prefix = pipeline ? buildTransformPrefix(pipeline.steps) : ""
    if (!prefix) return

    const pipelineName = pipeline?.name ?? "pipeline"

    try {
      // Drop the last run's reports first, so a request that throws can't
      // leave them showing under the weighting step.
      useWeightingReportStore.getState().setReports([])
      setIsRunningScript(true)
      // RESET so the run starts from a clean session; otherwise prior
      // state could clash with redefining a recode name.
      const script = `RESET\n${prefix}`
      const response = await RunEngineQuery(queryInstance, script, false)

      // The engine returns 200 even on script errors — the failure shows up
      // in `Err code` and as a non-success entry in `result.log`. Treat any
      // log line that isn't the literal "Script successfully executed." as
      // an error and surface the engine's own message to the user.
      const body = response.data as {
        "Err code"?: number
        result?: { log?: string[]; weighting?: WeightingReport[] }
      } | null
      const errCode = body?.["Err code"] ?? 0
      const logLines = body?.result?.log ?? []
      const lastLog = logLines[logLines.length - 1] ?? ""
      const reports = body?.result?.weighting ?? []
      useWeightingReportStore.getState().setReports(reports)
      const notes = reports.reduce((n, r) => n + r.notes.length, 0)
      const succeeded =
        errCode === 0 && lastLog === "Script successfully executed."

      if (succeeded) {
        await regenerateCodebook()
        notify.success(
          "Transform applied",
          notes > 0
            ? `"${pipelineName}" ran successfully, with ${notes} weighting note${notes === 1 ? "" : "s"}. See the weighting step.`
            : `"${pipelineName}" ran successfully.`
        )
      } else {
        // Prefer the engine's last log line for the description — it's the
        // line that actually tells the user *why* the run failed (e.g.
        // "QTable already exists: <<TITLE>>"). Fall back to the err code
        // when the log is empty.
        const detail =
          lastLog || `Engine returned Err code ${errCode}.`
        notify.error("Transform failed", detail)
      }
    } catch (error) {
      console.error("Error running transforms:", error)
      notify.error(
        "Transform failed",
        error instanceof Error ? error.message : "Unknown error"
      )
    } finally {
      setIsRunningScript(false)
    }
  }

  /**
   * "Run all" — concatenate every runnable Builder's script into one engine
   * query (single RESET, each Transform pipeline emitted once even when
   * multiple Builders share it) and stash the multi-output response in the
   * store via `setBatchResult`. The per-builder Apply path stays untouched.
   */
  const runBatch = useCallback(async (): Promise<void> => {
    if (!queryInstance) {
      console.warn("Cannot run batch: project not yet initialised")
      return
    }
    if (batchRunnableCount === 0 || !batchScript.trim()) {
      notify.error(
        "Nothing to run",
        "No Builders have both rows and columns yet."
      )
      return
    }
    const setBatchResult = useQueryBuilderStore.getState().setBatchResult
    const includedIdsSnapshot = [...batchBuilderIds]

    try {
      setIsRunningScript(true)
      const response = await RunEngineQuery(queryInstance, batchScript, false)

      // Match the success/error convention used by runTransforms — engine
      // returns 200 even on script errors, signalled via `Err code` and the
      // last `result.log` line.
      const body = response.data as
        | {
            "Err code"?: number
            result?: { log?: string[] }
          }
        | null
      const errCode = body?.["Err code"] ?? 0
      const logLines = body?.result?.log ?? []
      const lastLog = logLines[logLines.length - 1] ?? ""
      const succeeded =
        errCode === 0 && lastLog === "Script successfully executed."

      if (succeeded) {
        setBatchResult(
          response.data as EngineQueryResult,
          includedIdsSnapshot
        )
        await regenerateCodebook()
        notify.success(
          "Batch ran",
          `${includedIdsSnapshot.length} table${
            includedIdsSnapshot.length === 1 ? "" : "s"
          } produced.`
        )
      } else {
        const detail = lastLog || `Engine returned Err code ${errCode}.`
        notify.error("Batch run failed", detail)
      }
    } catch (error) {
      console.error("Error running batch:", error)
      notify.error(
        "Batch run failed",
        error instanceof Error ? error.message : "Unknown error"
      )
    } finally {
      setIsRunningScript(false)
    }
  }, [
    queryInstance,
    batchScript,
    batchBuilderIds,
    batchRunnableCount,
    RunEngineQuery,
    regenerateCodebook,
  ])

  /**
   * Walk the most recent batch result and write each OUTPUT to its own
   * sheet (mode === "separate") or stack them all into one sheet with
   * title rows between them (mode === "combined"), the same two modes as
   * the dashboard's Excel export. Each output is exported from its table
   * model (numbers with number formats, merged headers, frozen panes) with
   * the console table style.
   *
   * Sheet/title names come from the originating builder's user-facing
   * `name` (not the technical `outputName`); falls back to the engine's
   * OUTPUT label when a builder can't be matched by index.
   */
  const exportBatchToExcel = useCallback(
    async (mode: "separate" | "combined" = "separate"): Promise<void> => {
      const state = useQueryBuilderStore.getState()
      const batchResult = state.batchRunResult
      const builderIds = state.batchRunBuilderIds
      const outputs = batchResult?.result?.output ?? []
      if (outputs.length === 0) {
        notify.error(
          "Nothing to export",
          "Run the batch first — there are no results to write."
        )
        return
      }
      const projectName =
        project?.Name?.replace(/[^\w-]+/g, "_") || "Builder"
      const timestamp = new Date().toISOString().split("T")[0]

      try {
        // One `{ name, model }` per tabular output (an `RTableContent`).
        const tables: TableExport[] = []
        outputs.forEach((item, idx) => {
          const content = item.content as RTableContent | undefined
          if (!content || !Array.isArray(content.data)) return
          // Prefer the user-facing Builder name. The engine's OUTPUT label
          // equals the builder's `outputName`; match by index first
          // (script-emit order = output order) and fall back to engine label.
          const builderId = builderIds[idx]
          const builder = builderId
            ? state.builders.find((b) => b.id === builderId)
            : undefined
          tables.push({
            name: builder?.name || item.label || `Table ${idx + 1}`,
            model: buildTableModel(content),
          })
        })
        if (tables.length === 0) {
          notify.error(
            "Nothing to export",
            "The batch result has no tabular outputs."
          )
          return
        }

        // Read at export time, so the callback needs no new deps
        const workbook = buildWorkbook(tables, {
          mode,
          style: resolveTableStyle(
            "console",
            useTableStyleStore.getState().consoleChoice
          ),
        })
        await downloadWorkbook(
          workbook,
          mode === "separate"
            ? `${projectName}_builder_batch_${timestamp}`
            : `${projectName}_builder_batch_combined_${timestamp}`
        )
      } catch (error) {
        notify.error(
          "Excel export failed",
          error instanceof Error ? error.message : "Unknown error"
        )
      }
    },
    [project?.Name]
  )

  const pollForAsyncResult = async (
    asyncID: string,
    applyResult: (data: EngineQueryResult) => void
  ): Promise<void> => {
    try {
      const pollResult = await pollingAsync(asyncID)
      if (pollResult.status === 200) {
        const asyncResult = await getAsyncResult(asyncID)
        React.startTransition(() => {
          applyResult(asyncResult.data as EngineQueryResult)
          setIsRunningScript(false)
        })
      } else {
        setTimeout(() => pollForAsyncResult(asyncID, applyResult), 5000)
      }
    } catch (error) {
      console.error("Polling error:", error)
      setIsRunningScript(false)
    }
  }

  /**
   * Returns the script to save. Builder mode emits the generated script
   * directly; Console mode strips TipTap HTML back to plain text.
   */
  const getCurrentScriptForSave = (): string => {
    if (viewMode === "builder") return builderScript
    if (!editor) return ""
    return editor
      .getHTML()
      .replace(/<p>/g, "")
      .replace(/<\/p>/g, "\n")
      .replace(/&lt;&lt;/g, "<<")
      .replace(/&gt;&gt;/g, ">>")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&")
      .trim()
  }

  const saveProjectQuery = async (queryName: string): Promise<void> => {
    const query = getCurrentScriptForSave()
    if (!query.trim()) return

    try {
      // Use the structured save path so scripts also write a script-kind
      // sidecar. Critical when the user has just "Edit anyway"-ed a pipeline
      // or builder into Console — saving under a fresh name has to leave a
      // sidecar that explicitly says `kind: "script"`, otherwise the
      // bulk-meta-list would still classify the new record by whatever
      // sidecar exists on disk.
      await saveStructured(
        project.id,
        project.Created_by,
        queryName,
        query,
        { kind: "script" }
      )
      React.startTransition(() => {
        setContentChanged(false)
        fetchQueries()
        // Picking up where the user is: clear the structured-origin marker
        // so the next "Run/Save" cycle treats the editor as a regular new
        // script (no banner, no forced save-as).
        setConsoleStructuredOrigin(null)
        setSelectedQuery(queryName)
        notify.success(
          "Query saved successfully!",
          "View Saved Queries to access your saved queries."
        )
      })
    } catch (error) {
      console.error("Error saving query:", error)
    }
  }

  const handleConsoleSplitChange = useCallback(
    (sizes: number[]): void => {
      setConsoleSplitSizes(sizes)
    },
    [setConsoleSplitSizes]
  )

  // Leaving Console-as-viewer for the structured record's native tab.
  // Clearing the editor + selectedQuery here is critical: TipTap is
  // persistent across viewMode switches, so without these resets the script
  // would still be sitting in the editor when the user returns to Console,
  // and selectedQuery would still point at the pipeline — meaning a Save
  // would silently overwrite the pipeline's body with a flat script.
  const handleOpenStructuredInTab = useCallback(
    (kind: "pipeline" | "builder"): void => {
      if (editor) {
        editor.commands.clearContent()
      }
      setSelectedQuery("")
      setContentChanged(false)
      setConsoleStructuredOrigin(null)
      setViewMode(kind === "pipeline" ? "transform" : "builder")
    },
    [editor, setViewMode, setConsoleStructuredOrigin]
  )

  const consoleComponent = useMemo(
    () => (
      <Console
        editor={editor}
        result={result}
        scriptInProgress={scriptInProgress}
        asyncScriptInProgress={asyncScriptInProgress}
        splitSizes={consoleSplitSizes}
        onSplitChange={handleConsoleSplitChange}
        direction={consoleOutputDirection}
        order={consoleOutputOrder}
        onOpenStructuredInTab={handleOpenStructuredInTab}
      />
    ),
    [
      editor,
      result,
      scriptInProgress,
      asyncScriptInProgress,
      consoleSplitSizes,
      handleConsoleSplitChange,
      handleOpenStructuredInTab,
      consoleOutputDirection,
      consoleOutputOrder,
    ]
  )

  const handleSaveActiveBuilder = useCallback(
    async (saveName: string): Promise<void> => {
      if (!project?.id || !project.Created_by) return
      const activeId = useQueryBuilderStore.getState().activeBuilderId
      if (!activeId) return
      try {
        await saveBuilder(project.id, project.Created_by, activeId, saveName)
        notify.success(
          "Builder saved",
          `"${saveName}" was saved to the project.`
        )
        fetchQueries()
      } catch (err) {
        console.error("Failed to save builder:", err)
      }
    },
    [project?.id, project?.Created_by, saveBuilder, fetchQueries]
  )

  const handleDeleteActiveBuilder = useCallback(
    async (): Promise<void> => {
      const state = useQueryBuilderStore.getState()
      const activeId = state.activeBuilderId
      if (!activeId) return
      const savedName = state.builderSavedNames[activeId]
      state.removeBuilder(activeId)
      if (savedName && project?.id && project.Created_by) {
        try {
          await deleteQuery(project.id, project.Created_by, savedName)
          fetchQueries()
        } catch (err) {
          console.error(
            "Builder removed locally but backend delete failed:",
            err
          )
        }
      }
    },
    [project?.id, project?.Created_by, deleteQuery, fetchQueries]
  )

  const builderComponent = useMemo(
    () => (
      <QueryBuilder
        scriptInProgress={scriptInProgress}
        asyncScriptInProgress={asyncScriptInProgress}
        splitSizes={consoleSplitSizes}
        onSplitChange={handleConsoleSplitChange}
        direction={consoleOutputDirection}
        order={consoleOutputOrder}
        onSaveActiveBuilder={handleSaveActiveBuilder}
        onDeleteActiveBuilder={handleDeleteActiveBuilder}
        onRunBatch={runBatch}
        onExportBatch={exportBatchToExcel}
        batchRunnableCount={batchRunnableCount}
      />
    ),
    [
      scriptInProgress,
      asyncScriptInProgress,
      consoleSplitSizes,
      handleConsoleSplitChange,
      consoleOutputDirection,
      consoleOutputOrder,
      handleSaveActiveBuilder,
      handleDeleteActiveBuilder,
      runBatch,
      exportBatchToExcel,
      batchRunnableCount,
    ]
  )

  const savedQueriesComponent = useMemo(
    () => (
      <SavedQueries
        project={project}
        toggleSaved={() => setViewMode("console")}
        projectQueries={projectQueries}
        editor={editor}
        refreshQueries={fetchQueries}
        setSelectedQuery={setSelectedQuery}
        selectedQuery={selectedQuery}
        setContentChanged={setContentChanged}
        onLoadNames={onLoadList}
      />
    ),
    [
      project,
      projectQueries,
      editor,
      selectedQuery,
      setSelectedQuery,
      setContentChanged,
      setViewMode,
      fetchQueries,
      onLoadList,
    ]
  )

  // No useMemo here: runTransforms closes over queryInstance, which is set
  // asynchronously by getInitialData. Memoising on isRunningScript captured
  // a stale runTransforms with queryInstance=null until the first Builder
  // run forced the memo to refresh. Re-creating the JSX every render is
  // cheap and TransformView reads its own state from the store.
  const handleSaveActivePipeline = useCallback(
    async (saveName: string): Promise<void> => {
      if (!project?.id || !project.Created_by) return
      const activeId =
        useQueryBuilderStore.getState().activeTransformPipelineId
      if (!activeId) return
      try {
        await savePipeline(project.id, project.Created_by, activeId, saveName)
        notify.success(
          "Pipeline saved",
          `"${saveName}" was saved to the project.`
        )
        // Refresh queries so the SavedQueries list shows the new entry.
        fetchQueries()
      } catch (err) {
        console.error("Failed to save pipeline:", err)
      }
    },
    [project?.id, project?.Created_by, savePipeline, fetchQueries]
  )

  const handleDeleteActivePipeline = useCallback(
    async (): Promise<void> => {
      const state = useQueryBuilderStore.getState()
      const activeId = state.activeTransformPipelineId
      if (!activeId) return
      // Capture the saved name BEFORE removing the local entry — the store
      // wipes `pipelineSavedNames[activeId]` as part of removeTransformPipeline.
      const savedName = state.pipelineSavedNames[activeId]
      state.removeTransformPipeline(activeId)
      if (savedName && project?.id && project.Created_by) {
        try {
          await deleteQuery(project.id, project.Created_by, savedName)
          fetchQueries()
        } catch (err) {
          console.error(
            "Pipeline removed locally but backend delete failed:",
            err
          )
        }
      }
    },
    [project?.id, project?.Created_by, deleteQuery, fetchQueries]
  )

  const transformComponent = (
    <TransformView
      onApply={runTransforms}
      isApplying={isRunningScript}
      onSaveActivePipeline={handleSaveActivePipeline}
      onDeleteActivePipeline={handleDeleteActivePipeline}
      codebook={codebook}
    />
  )

  // Plain value, not memoised: transformComponent is intentionally re-
  // created each render so it has a fresh closure over queryInstance, so
  // any memo here would invalidate every render anyway.
  let rightPaneContent: React.ReactNode
  switch (viewMode) {
    case "builder":
      rightPaneContent = builderComponent
      break
    case "transform":
      rightPaneContent = transformComponent
      break
    case "saved":
      rightPaneContent = savedQueriesComponent
      break
    case "console":
    default:
      rightPaneContent = consoleComponent
  }

  const dataTreePane = (
    <div className="w-1/4 border flex flex-col overflow-hidden">
      <DataTree
        editor={editor}
        codebook={codebook}
        isCodebookLoading={isCodebookLoading}
        regenerateCodebook={regenerateCodebook}
        // Click-to-insert only in console mode: in the other modes the editor
        // is hidden, so inserted text would pile up unseen. Drag and drop is
        // the way to feed variables into the Builder.
        onVariableClick={
          viewMode === "console" && editor
            ? (path) => editor.commands.insertContent(`<<${path}>> `)
            : undefined
        }
      />
    </div>
  )

  const rightPane = (
    <div className="flex-1 flex flex-col overflow-hidden">
      {rightPaneContent}
    </div>
  )

  const mainSplitView = useMemo(() => {
    if (!dataTreeVisible) {
      return <div className="flex flex-grow overflow-hidden">{rightPane}</div>
    }

    return (
      <Split
        className="flex flex-grow overflow-hidden"
        direction="horizontal"
        gutterSize={6}
        sizes={horizontalSplitSizes}
        onDragEnd={setHorizontalSplitSizes}
        minSize={100}
      >
        {dataTreePane}
        {rightPane}
      </Split>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataTreeVisible,
    horizontalSplitSizes,
    setHorizontalSplitSizes,
    editor,
    viewMode,
    codebook,
    isCodebookLoading,
    regenerateCodebook,
    rightPaneContent,
  ])

  if (pageLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader fontSize={48} />
        {!editor
          ? "Loading Editor..."
          : isGettingDataFilesUploaded
          ? "Checking Data File..."
          : isInitialisingData
          ? "Initializing Data File..."
          : "Loading"}
      </div>
    )
  }

  if (!isDataLoading && !dataUploaded) {
    return (
      <EmptyState message="No Data File Found! Please go to Data Import tab and import/generate the data file" />
    )
  }

  // The assistant's panel docks inside the console's root, so it stays beside
  // the console when the console is maximised. Without a project id nothing
  // registers, so no turn is sent from here.
  return (
    <ConfigProvider theme={QC_THEME}>
      <div
        className="query-console-root dq-workspace flex flex-col h-full"
        data-maximized={isMaximized || undefined}
      >
        <AssistantDock projectId={project.id} owner={project.Created_by}>
          {project.id && <AssistantScreenRegistrar registration={CONSOLE_ASSISTANT} />}
          <div className="flex flex-col h-full min-h-0">
            <QueryConsoleToolbar
              projectQueries={projectQueries}
              selectedQuery={selectedQuery}
              setSelectedQuery={setSelectedQuery}
              editor={editor}
              contentChanged={contentChanged}
              setContentChanged={setContentChanged}
              queryInstance={queryInstance}
              runScript={runScript}
              isRunningScript={isRunningScript}
              canRun={viewMode === "builder" ? builderRunnable : true}
              saveProjectQuery={saveProjectQuery}
              activeKey={activeKey}
              forceSaveAs={consoleStructuredOrigin !== null}
              runOnLoadOn={onLoadList.length > 0}
              onOpenRunOnLoad={() => setRunOnLoadOpen(true)}
              onOpenLineage={() => setLineageOpen(true)}
              onOpenVersionCheck={() =>
                navigate(provenanceLink(project.id, "stamp"))
              }
              headerActions={project.id ? <AssistantToggle /> : undefined}
            />
            <RunOnLoadBanner
              failure={onLoadFailure ? runOnLoadFailureText(onLoadFailure) : null}
              namesScript={Boolean(onLoadFailure?.script)}
              withoutEdits={withoutEdits}
              onReload={() => void reloadData(withoutEdits ? "off" : "report")}
              onTurnEditsOn={toggleWithoutEdits}
            />
            {mainSplitView}
          </div>
        </AssistantDock>
      </div>
      <ConfirmationModal
        title="Replace the script in the editor?"
        isVisible={replacing !== null}
        message="The editor has changes that are not saved. Opening the assistant's script replaces them."
        confirmButtonText="Replace"
        confirmButtonType="danger"
        cancel={() => setReplacing(null)}
        confirm={() => {
          if (replacing) loadHandoff(replacing)
          setReplacing(null)
        }}
      />
      <LineageModal
        open={lineageOpen}
        onClose={() => setLineageOpen(false)}
        queryInstance={queryInstance}
        projectId={project.id}
        runExclusive={lineageInTurn}
      />
      <RunOnLoadModal
        open={runOnLoadOpen}
        available={onLoadAvailable}
        list={onLoadList}
        listError={onLoadListError}
        listReplaceable={onLoadListReplaceable}
        withoutEdits={withoutEdits}
        onClose={() => setRunOnLoadOpen(false)}
        onSave={saveRunOnLoad}
        check={runOnLoad.check}
        // Each closes the modal first, so the reload's loader does not remount it open.
        onReload={() => {
          setRunOnLoadOpen(false)
          void reloadData(withoutEdits ? "off" : "report")
        }}
        onToggleWithoutEdits={() => {
          setRunOnLoadOpen(false)
          toggleWithoutEdits()
        }}
      />
    </ConfigProvider>
  )
}

export default QueryConsoleContainer
